import { DateTime } from "luxon";
import axios, { CancelTokenSource, InternalAxiosRequestConfig } from "axios";

import { ActionResponse, ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { Cancelled } from "../../types/ErrorTypes";
import { AuthState } from "../../state/reducers/authReducer";
import { API_BASE_URL } from "../../config";
import { parseDateTimes } from "../../utilities/helpers/parseDateTimes";

export class AjaxController {
  private baseUrl = API_BASE_URL;
  private cancelTokenSources: { [key: string]: CancelTokenSource } = {};
  private getAuthState?: () => AuthState | null;
  private refreshTokenCallback?: () => Promise<{ accessToken?: string } | null | undefined>;
  private refreshPromise: Promise<{ accessToken: string } | null> | null = null;
  private authFailureCallback?: () => void;
  private authFailureNotified = false;
  private client = axios.create({ baseURL: this.baseUrl });

  constructor() {
    this.client.interceptors.request.use((config) => {
      if (config.data && !(config.data instanceof FormData)) {
        config.data = convertDatesToUTC(config.data);
      }
      return config;
    });

    this.client.interceptors.response.use(
      originalResponse => {
        // A successful authenticated response means we're healthy again.
        this.authFailureNotified = false;
        parseDateTimes(originalResponse.data);
        return originalResponse;
      },
      async (error) => {
        // Reactively recover from 401s (e.g. a token invalidated server-side by a
        // deploy/restart, which the client-side expiry check can't anticipate):
        // refresh once and retry the original request; if that fails, sign out.
        if (!axios.isAxiosError(error) || error.response?.status !== 401) {
          return Promise.reject(error);
        }

        const original = error.config as (InternalAxiosRequestConfig & { _authRetried?: boolean }) | undefined;

        // Never try to recover the auth endpoints themselves; that would loop.
        if (!original || this.isAuthEndpoint(original.url) || original._authRetried) {
          if (original && !this.isAuthEndpoint(original.url)) this.notifyAuthFailure();
          return Promise.reject(error);
        }

        original._authRetried = true;
        const refreshed = await this.forceRefresh();
        if (!refreshed) {
          this.notifyAuthFailure();
          return Promise.reject(error);
        }

        original.headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
        return this.client(original);
      }
    );
  }

  setTokenGetter(getter: () => AuthState | null) {
    this.getAuthState = getter;
  }

  setTokenRefreshCallback(cb: () => Promise<{ accessToken?: string } | null | undefined>) {
    this.refreshTokenCallback = cb;
  }

  /** Registered by the store: invoked once when the session can't be recovered
   *  (refresh failed), so the app can sign the user out and redirect to login. */
  setAuthFailureHandler(cb: () => void) {
    this.authFailureCallback = cb;
  }

  private isAuthEndpoint(url?: string): boolean {
    if (!url) return false;
    return url.includes("/User/login") || url.includes("/User/refresh");
  }

  private notifyAuthFailure() {
    if (this.authFailureNotified) return;
    this.authFailureNotified = true;
    this.authFailureCallback?.();
  }

  /** Refreshes the access token, de-duplicating concurrent callers. Returns null
   *  when no refresh is possible or it fails. */
  private forceRefresh(): Promise<{ accessToken: string } | null> {
    const state = this.getAuthState?.();
    if (!this.refreshTokenCallback || !state?.refreshToken) return Promise.resolve(null);

    if (!this.refreshPromise) {
      this.refreshPromise = Promise.resolve()
        .then(() => this.refreshTokenCallback!())
        .then(t => (t && t.accessToken ? { accessToken: t.accessToken } : null))
        .catch(() => null)
        .finally(() => { this.refreshPromise = null; });
    }
    return this.refreshPromise;
  }

  private async ensureValidToken(): Promise<string | null> {
    const state = this.getAuthState?.();
    if (state == null || !state.accessToken || !state.expiresAt || !state.isAuthenticated) return null;

    const isExpiringSoon = DateTime.fromMillis(state.expiresAt).diffNow("milliseconds").milliseconds < 60_000;
    if (isExpiringSoon) {
      const refreshed = await this.forceRefresh();
      return refreshed?.accessToken ?? state.accessToken;
    }
    return state.accessToken;
  }

  private async getAuthHeaders() {
    const token = await this.ensureValidToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async get<T>(endpoint: string, params: object, transformResponse?: (raw: object) => T, transformRequest?: (params: object) => object): Promise<T | ErrorResponse> {
    try {
      this.cancelPreviousRequest(endpoint);
      const rawResult = await this.client.get<T>(`${this.baseUrl}${endpoint}`,
        {
          params: transformRequest ? transformRequest(params) : params,
          cancelToken: this.createNewCancelToken(endpoint).token,
          withCredentials: true,
          headers: await this.getAuthHeaders()
        });
      if (transformResponse) {
        return transformResponse(rawResult.data as any);
      }
      return rawResult.data;
    } catch (error) {
      if (axios.isCancel(error))
        throw Cancelled;
      else //Note: We don't need to log request cancellations
        console.log(error)

      if (axios.isAxiosError(error) && error.response) {
        return Promise.reject(
          new ErrorResponse(error.response.status, [
            {
              code: error.response.status,
              description: error.response.data.message || 'An error occurred',
            },
          ])
        );
      }
      return Promise.reject(
        new ErrorResponse(500, [
          {
            code: 500,
            description: 'An error occurred',
          },
        ])
      );
    }
  }

  async post<T, T1>(endpoint: string, data: T, transformRequest?: (data: T) => T, skipAuth?: boolean): Promise<ActionResponse<T1 | null>> {
    try {
      if (transformRequest) {
        data = transformRequest(data);
      }

      const result = await this.client.post<T1>(`${this.baseUrl}${endpoint}`, data, {
        withCredentials: true,
        headers: skipAuth ? {} : await this.getAuthHeaders()
      });

      return result.data as ActionResponse<T1>;
    } catch (error) {
      console.log(error)
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          data: null,
          errors: [
            {
              code: error.response.status,
              description: error.response.data.message || 'An error occurred',
            },
          ]
        } as ActionResponse<T1>;
      }
      return Promise.reject(
        {
          success: false,
          data: null,
          errors: [
            {
              code: 500,
              description: 'An error occurred',
            },
          ]
        } as ActionResponse<T1>
      );
    }
  }

  async put<TIn, TOut = TIn>(endpoint: string, data: TIn, transformRequest?: (data: TIn) => TIn): Promise<ActionResponse<TOut | null>> {
    try {
      if (transformRequest) {
        data = transformRequest(data);
      }

      const response = await this.client.put<TOut>(`${this.baseUrl}${endpoint}`, data, {
        withCredentials: true,
        headers: await this.getAuthHeaders()
      });

      return response.data as ActionResponse<TOut>;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          data: null,
          errors: [
            {
              code: error.response.status,
              description: error.response.data.message || 'An error occurred',
            },
          ]
        } as ActionResponse<TOut>;
      }

      return Promise.reject(
        {
          success: false,
          data: null,
          errors: [
            {
              code: 500,
              description: 'An error occurred',
            },
          ]
        } as ActionResponse<TOut>
      );
    }
  }

  async delete<T = null>(endpoint: string): Promise<ActionResponse<T>> {
    try {
      const response = await this.client.delete<ActionResponse<T>>(`${this.baseUrl}${endpoint}`, {
        withCredentials: true,
        headers: await this.getAuthHeaders()
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          data: null,
          messages: [
            {
              code: error.response.status.toString(),
              description: error.response.data.message || 'An error occurred',
            },
          ]
        } as ActionResponse<T>;
      }
      return Promise.reject(
        {
          success: false,
          data: null,
          messages: [
            {
              code: '500',
              description: 'An error occurred',
            },
          ]
        } as ActionResponse<T>
      );
    }
  }

  async downloadFile(endpoint: string, filename: string): Promise<void> {
    try {
      const response = await this.client.get(`${this.baseUrl}${endpoint}`, {
        responseType: 'blob',
        headers: await this.getAuthHeaders(),
        withCredentials: true
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return Promise.reject(
          new ErrorResponse(error.response.status, [
            {
              code: error.response.status,
              description: error.response.data.message || 'Download failed',
            },
          ])
        );
      }

      return Promise.reject(
        new ErrorResponse(500, [
          {
            code: 500,
            description: 'Unexpected download error',
          },
        ])
      );
    }
  }

  private cancelPreviousRequest(endpoint: string) {
    if (this.cancelTokenSources[endpoint]) {
      this.cancelTokenSources[endpoint].cancel('Request cancelled due to a new request.');
    }
  }

  private createNewCancelToken(endpoint: string): CancelTokenSource {
    this.cancelTokenSources[endpoint] = axios.CancelToken.source();
    return this.cancelTokenSources[endpoint];
  }
}

function convertDatesToUTC(obj: any): any {
  if (obj === null || obj === undefined) return obj;

  if (obj instanceof FormData || obj instanceof File || obj instanceof Blob) {
    return obj;
  }

  if (obj instanceof Date) {
    return DateTime.fromJSDate(obj).toUTC().toISO();
  }

  if (obj instanceof DateTime) {
    return obj.toUTC().toISO();
  }

  if (Array.isArray(obj)) {
    return obj.map(convertDatesToUTC);
  }

  if (typeof obj === "object") {
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertDatesToUTC(value);
    }
    return result;
  }

  return obj;
}

/**
 * Iterates through an object and formats all Javascript DateTime objects to only include the date (not time) in ISO format (YYYY-MM-DD).
 * This is to match the DateOnly format in the backend.
 * If endpoint doesn't expect DateOnly or object does not have DateTime objects, then this function should not be called.
 *
 * @param obj The object to format dates in
 * @returns A new object with dates formatted
 */
export function formatDatesForDateOnlyModel<T extends object>(obj: T): T {
  function formatAsLocalDateString(date: DateTime): string {
    return date.toFormat("yyyy-MM-dd");
  }

  function recurse(value: any): any {
    if (DateTime.isDateTime(value)) {
      return value.toISODate(); // returns "YYYY-MM-DD"
    }

    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      if (parsed.isValid) {
        return formatAsLocalDateString(parsed); // or any desired format
      }
    }

    if (Array.isArray(value)) {
      return value.map(recurse);
    }

    if (value && typeof value === 'object') {
      const result: any = {};
      for (const key in value) {
        result[key] = recurse(value[key]);
      }
      return result;
    }
    return value;
  }

  return recurse(obj);
}

/**
 * Recursively walks an object (and any nested arrays/objects),
 * and replaces every DateTime instance with a time‐only string "HH:mm:ss".
 * Call this in your transformRequest for endpoints expecting TimeOnly values.
 */
export function formatDatesForTimeOnlyModel<T extends object>(obj: T): T {
  function recurse(value: any): any {
    if (value instanceof DateTime) {
      // take the time portion of the ISO and drop the trailing Z
      return value.toUTC().toFormat('HH:mm:ss');
    } else if (Array.isArray(value)) {
      return value.map(recurse);
    } else if (value && typeof value === 'object') {
      const out: any = {};
      for (const k of Object.keys(value)) {
        out[k] = recurse((value as any)[k]);
      }
      return out;
    }
    return value;
  }

  return recurse(obj);
}

export const ajaxController = new AjaxController();